import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;

    if (isStandalone) return;

    // Check if user dismissed recently
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const hoursSince = (Date.now() - dismissedAt) / (1000 * 60 * 60);
      if (hoursSince < 24) return;
    }

    // If prompt is already available
    if (window.deferredPrompt) {
      setShowBanner(true);
      return;
    }

    // Listen for install prompt
    const handleReady = () => setShowBanner(true);
    const handleInstalled = () => setShowBanner(false);

    window.addEventListener('pwaInstallReady', handleReady);
    window.addEventListener('pwaInstalled', handleInstalled);

    return () => {
      window.removeEventListener('pwaInstallReady', handleReady);
      window.removeEventListener('pwaInstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = window.deferredPrompt;
    if (!prompt) return;

    setInstalling(true);
    prompt.prompt();
    const result = await prompt.userChoice;

    if (result.outcome === 'accepted') {
      setShowBanner(false);
      // Record the install
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await axios.post(`${API_URL}/api/pwa/install`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch {}
    }
    window.deferredPrompt = null;
    setInstalling(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', String(Date.now()));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-500" data-testid="pwa-install-banner">
      <div className="bg-[#0B1F3A] rounded-2xl p-4 shadow-2xl border border-[#2272C5]/30">
        <button onClick={handleDismiss} className="absolute top-3 right-3 text-white/50 hover:text-white" data-testid="dismiss-pwa-banner">
          <X size={18} />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#2272C5]/20 flex items-center justify-center flex-shrink-0">
            <Smartphone size={24} className="text-[#38B6CC]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm">Install AegisPay</p>
            <p className="text-white/60 text-xs">Add to home screen for the best experience</p>
          </div>
          <Button
            onClick={handleInstall}
            disabled={installing}
            className="bg-[#2272C5] hover:bg-[#2272C5]/90 text-white rounded-xl px-4 text-xs font-bold flex-shrink-0"
            size="sm"
            data-testid="install-pwa-button"
          >
            <Download size={14} className="mr-1" />
            {installing ? '...' : 'Install'}
          </Button>
        </div>
      </div>
    </div>
  );
};
